/**
 * Position label formatting utility
 *
 * Formats player position abbreviations into grammatically correct labels
 * for chart legends. Filters out null/undefined values to prevent the
 * trailing "and" bug where positions arrays contain sparse entries.
 * @see SE-3761 — Fixes "Our LW and RW and" → "Our LW and RW"
 * @module utils/formatPositionLabel
 */

/**
 * Formats position abbreviations into a grammatically correct "Our ..." label
 *
 * Handles null/undefined filtering to prevent trailing connectives when
 * the positions array contains sparse entries from the GraphQL response.
 * @param positions - Array of position abbreviations, possibly containing null/undefined
 * @returns Formatted label like "Our LW and RW" or "Our GKs"
 * @example
 * formatPositionLabel(["LW", "RW"])        // "Our LW and RW"
 * formatPositionLabel(["GK", undefined])   // "Our GKs"
 * formatPositionLabel(["LW", "RW", "ST"])  // "Our LW, RW and ST"
 * formatPositionLabel([])                  // "Our Positions"
 */
export const formatPositionLabel = (
  positions: readonly (string | null | undefined)[]
): string => {
  const filtered = positions.filter((p): p is string => p != null && p !== "");

  if (filtered.length === 0) return "Our Positions";
  if (filtered.length === 1) return `Our ${filtered[0]}s`;

  return `Our ${filtered.slice(0, -1).join(", ")} and ${filtered[filtered.length - 1]}`;
};
